import type { LaunchParams, User } from "./telegramTypes";

type PartialLaunchParams = Omit<Partial<LaunchParams>, "initData"> & {
  initData?: Partial<NonNullable<LaunchParams["initData"]>>;
};

type TelegramWebApp = {
  platform?: string;
  initData?: string;
  initDataUnsafe?: {
    user?: User;
    start_param?: string;
  };
};

const TELEGRAM_INIT_DATA_SESSION_CACHE_KEY = "recipmon:telegram-initdata-raw";
const DEFAULT_INIT_DATA_WAIT_INTERVAL_MS = 120;
const TELEGRAM_QUERY_KEYS = [
  "tgWebAppData",
  "tgWebAppVersion",
  "tgWebAppPlatform",
  "tgWebAppStartParam",
  "tgWebAppThemeParams",
] as const;

const parseJson = <T>(value: string | null): T | null => {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

const getTelegramWebApp = (): TelegramWebApp | null => {
  if (typeof window === "undefined") return null;
  const globalWindow = window as typeof window & {
    Telegram?: { WebApp?: TelegramWebApp };
  };
  return globalWindow.Telegram?.WebApp ?? null;
};

const normalizeValue = (value: string | null | undefined) => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const extractHashQuery = (hash: string) => {
  const normalizedHash = normalizeValue(hash.replace(/^#/, ""));
  if (!normalizedHash) return null;

  const queryIndex = normalizedHash.indexOf("?");
  if (queryIndex >= 0) {
    return normalizeValue(normalizedHash.slice(queryIndex + 1));
  }

  if (normalizedHash.includes("=")) {
    return normalizedHash;
  }

  return null;
};

const getHashParams = (hash: string) => {
  const query = extractHashQuery(hash);
  if (!query) return null;
  return new URLSearchParams(query);
};

const getSearchAndHashParams = () => {
  if (typeof window === "undefined") {
    return {
      search: null,
      hash: null,
    } as const;
  }

  return {
    search: new URLSearchParams(window.location.search),
    hash: getHashParams(window.location.hash),
  } as const;
};

const getParamFromSearchOrHash = (
  searchParams: URLSearchParams | null,
  hashParams: URLSearchParams | null,
  key: string,
) => {
  const fromSearch = normalizeValue(searchParams?.get(key) ?? null);
  if (fromSearch) return fromSearch;
  return normalizeValue(hashParams?.get(key) ?? null);
};

const getSafeSessionStorage = () => {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const hasRequiredInitDataFields = (initData: string) => {
  try {
    const params = new URLSearchParams(initData);
    return Boolean(
      normalizeValue(params.get("auth_date")) &&
        normalizeValue(params.get("hash")),
    );
  } catch {
    return false;
  }
};

const readCachedInitDataRaw = () => {
  const storage = getSafeSessionStorage();
  if (!storage) return null;

  try {
    const cached = normalizeValue(storage.getItem(TELEGRAM_INIT_DATA_SESSION_CACHE_KEY));
    if (!cached) return null;
    if (!hasRequiredInitDataFields(cached)) {
      storage.removeItem(TELEGRAM_INIT_DATA_SESSION_CACHE_KEY);
      return null;
    }
    return cached;
  } catch {
    return null;
  }
};

const cacheInitDataRawIfValid = (initData: string | null) => {
  const normalized = normalizeValue(initData);
  if (!normalized || !hasRequiredInitDataFields(normalized)) {
    return;
  }

  const storage = getSafeSessionStorage();
  if (!storage) return;

  try {
    storage.setItem(TELEGRAM_INIT_DATA_SESSION_CACHE_KEY, normalized);
  } catch {
    // Ignore storage write failures.
  }
};

const fromWebAppGlobal = (): PartialLaunchParams | null => {
  const webApp = getTelegramWebApp();
  if (!webApp) return null;

  const startParam = webApp.initDataUnsafe?.start_param ?? null;
  const user = webApp.initDataUnsafe?.user ?? null;

  return {
    platform: webApp.platform,
    startParam: startParam ?? undefined,
    initData: {
      startParam: startParam ?? undefined,
      user: user ?? undefined,
    },
  };
};

const fromSearchParams = (): PartialLaunchParams | null => {
  if (typeof window === "undefined") return null;

  const { search, hash } = getSearchAndHashParams();
  const tgStartParam = getParamFromSearchOrHash(search, hash, "tgWebAppStartParam");
  const tgPlatform = getParamFromSearchOrHash(search, hash, "tgWebAppPlatform");
  const tgWebAppData = getParamFromSearchOrHash(search, hash, "tgWebAppData");

  if (!tgStartParam && !tgPlatform && !tgWebAppData) {
    return null;
  }

  const initDataParams = tgWebAppData ? new URLSearchParams(tgWebAppData) : null;
  const initDataUser = parseJson<User>(initDataParams?.get("user") ?? null);
  const initDataStartParam = initDataParams?.get("start_param") ?? null;
  const startParam = tgStartParam ?? initDataStartParam;

  return {
    platform: tgPlatform ?? undefined,
    startParam: startParam ?? undefined,
    initData: {
      startParam: startParam ?? undefined,
      user: initDataUser ?? undefined,
    },
  };
};

export const getTelegramLaunchParams = (): LaunchParams | null => {
  if (typeof window === "undefined") {
    return null;
  }

  const launchParams = fromWebAppGlobal() ?? fromSearchParams();
  if (!launchParams) {
    return null;
  }

  return launchParams as LaunchParams;
};

export const getTelegramUser = (): User | null => {
  return getTelegramLaunchParams()?.initData?.user ?? null;
};

export const getTelegramInitDataRaw = (): string | null => {
  const webApp = getTelegramWebApp();
  const webAppInitData = webApp?.initData;
  const fromWebApp = normalizeValue(webAppInitData);
  if (fromWebApp) {
    cacheInitDataRawIfValid(fromWebApp);
    return fromWebApp;
  }

  if (typeof window === "undefined") {
    return null;
  }

  const { search, hash } = getSearchAndHashParams();
  const fromLocation = getParamFromSearchOrHash(search, hash, "tgWebAppData");
  if (fromLocation) {
    if (hasRequiredInitDataFields(fromLocation)) {
      cacheInitDataRawIfValid(fromLocation);
      return fromLocation;
    }

    const cached = readCachedInitDataRaw();
    if (cached) return cached;
    return fromLocation;
  }

  return readCachedInitDataRaw();
};

export const getTelegramStartParam = (): string | null => {
  const launchParams = getTelegramLaunchParams();
  return launchParams?.startParam ?? launchParams?.initData?.startParam ?? null;
};

export type WaitForTelegramInitDataRawOptions = {
  timeoutMs?: number;
  intervalMs?: number;
};

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

export const waitForTelegramInitDataRaw = async (
  options?: WaitForTelegramInitDataRawOptions,
) => {
  const immediate = getTelegramInitDataRaw();
  if (immediate || typeof window === "undefined") {
    return immediate;
  }

  const timeoutMs = Math.max(0, options?.timeoutMs ?? 0);
  if (timeoutMs === 0) {
    return null;
  }

  const intervalMs = Math.max(
    25,
    options?.intervalMs ?? DEFAULT_INIT_DATA_WAIT_INTERVAL_MS,
  );
  const startedAt = Date.now();
  let current = immediate;

  while (!current && Date.now() - startedAt < timeoutMs) {
    await sleep(intervalMs);
    current = getTelegramInitDataRaw();
  }

  return current;
};

export const isTelegramEnvironment = (): boolean => {
  if (typeof window === "undefined") return false;

  // The Telegram Web App script may be loaded even outside the real TMA context
  // (e.g. in preview environments). Require actual initData or query params to
  // avoid false positives.
  const webApp = getTelegramWebApp();
  if (webApp) {
    // If the SDK has initData it's a real TMA session
    if (typeof webApp.initData === "string" && webApp.initData.trim().length > 0) {
      return true;
    }
    // Some older platforms may not expose initData but set a meaningful platform
    if (webApp.platform && webApp.platform !== "unknown") {
      return true;
    }
  }

  const { search, hash } = getSearchAndHashParams();
  if (!search && !hash) return false;

  return TELEGRAM_QUERY_KEYS.some(
    (key) => Boolean(search?.has(key) || hash?.has(key)),
  );
};
